import { Controller } from "@hotwired/stimulus"
import { get, post, patch, destroy } from "@rails/request.js"
import { Modal } from "bootstrap"
import { setValueOrEmpty } from "../lib/empty_value"
import { setButtonLoading } from "../lib/button_loading"
import { AddCustomFieldIsland } from "../lib/add_custom_field_island"
import { showConfirmModal } from "../lib/custom_confirm"
import { syncGateReason } from "../lib/gate_reason"
import { emptyValueElement } from "../lib/empty_value"
import {
  applySavedColumns, columnsForSave, appliedSentence, appliedBullets, appliedWhen, formatLastUsed
} from "../lib/import_saved_mappings"
import { statusBadgeClass } from "../lib/status_badge"

// Drives the /import_leads wizard (#13306; executor #15364). One controller on
// the page shell spans the step card and its action bar:
//
//   upload  — reads the chosen CSV IN THE BROWSER (header + first rows + row
//             count), validates its shape and size, auto-matches recognisable
//             headers, keeps the order name / lead type / cost / vendor / mode,
//             gates Continue.
//   map     — renders one row per column from the draft through the HAML
//             <template>, derives Mapped / Ignored / Not mapped / Missing
//             statuses, gates Continue on the required fields.
//   verify  — fills the review summary + mapping table from the draft, toggles
//             the tags / Workflow controls, gates Start Import, and on Start
//             Import POSTs the file + every choice to import_leads#create.
//   results — server-rendered from the import record; Start Over / Import
//             Another File clear the draft.
//
// The draft lives in sessionStorage, and so does the file's TEXT (its own key,
// CSV_STORAGE_KEY): the browser cannot carry a File across the steps' page
// visits, and nothing is uploaded before Start Import. The server re-parses
// and re-validates everything (LeadImports::Submission); the mapping is posted
// by column INDEX so the server never re-derives the header keying below.
// Contracts: docs/runbooks/import-leads-ui-backend-handoff.md.
//
// Import from GHL (#15701) is the Upload step's other source. A GHL file is
// held on the SERVER — the export is uploaded at once (import_leads#
// create_ghl_source), or Connect GHL pulls the location's contacts and sends
// the agent back with ?ghl_source_id=, which this controller polls until the
// pull is ready. The draft keeps the server's preview (same shape as a
// browser-read CSV) plus `ghlSourceId`, and Start Import posts that id instead
// of a file. Everything from Map Fields on is the same for both sources.
//
// Saved mappings (#19586) live on Map Fields: a saved mapping that fits the
// file (the server's LeadImports::SavedMappingMatcher — same GHL account, or
// at least 90% of its column names) is applied once per file with a notice and
// Use Automatic Matching to undo it; the picker applies any other; Continue
// asks to save a new or changed mapping; Manage Mappings renames and deletes.
// The pure half is lib/import_saved_mappings.js.
//
// Tags (#19588): "Add tags to imported leads" is the shared multi-tag picker
// (React TagListPicker, the workflow Tag "Is Any Of" editor) mounted as the
// react-tag-list-picker-bridge island. It talks to this controller through
// the tag-list-picker:* DOM events (mountTagListPicker.tsx): `change` carries
// the chosen names into draft.tags, `ready` asks for the saved state, and
// this controller answers with `set` (the names + whether the checkbox is
// ticked). A tag created in the picker is saved to the agent's own list at
// once (POST /tags.json), so the names posted as tag_names[] pass
// LeadImports::Submission's own-tag check.

export const STORAGE_KEY = "goat.import_leads.draft"
export const TAG_PICKER_SET_EVENT = "tag-list-picker:set"
export const IGNORE_VALUE = "__ignore__"
// The Map Fields choice that opens "Create custom field" — never a mapping
// (= ImportLeadsHelper::CREATE_CUSTOM_FIELD_VALUE).
export const CREATE_CUSTOM_VALUE = "__create_custom__"
export const SAMPLE_ROWS = 3
export const UPDATE_MODES = ["create_and_update", "update_only"]
export const CONSENT_REASON = "Confirm consent first"
export const REQUIRED_REASON = "Map the required fields first"
export const DUPLICATE_REASON = "Each field can be mapped to only one column"
export const NO_FILE_REASON = "Choose a CSV file first"
export const CSV_STORAGE_KEY = "goat.import_leads.csv"
export const SETTINGS_REASON = "Finish the Upload step first"
export const STALE_FILE_REASON = "Choose the file again on the Upload step"
export const SUBMIT_FAILED = "The import could not be started. Check your connection and try again."
export const GHL_SOURCE = "ghl"
export const CSV_SOURCE = "csv"
export const GHL_POLL_MS = 3000
export const GHL_UPLOADING = "Uploading your GHL export…"
export const GHL_PULLING = "Reading your contacts from GHL. This can take a few minutes for a large account…"
export const GHL_UPLOAD_FAILED = "The GHL export could not be uploaded. Check your connection and try again."
export const GHL_PULL_FAILED = "We could not check on your GHL contacts. Reload the page to try again."

// The progress popup (#17615). Agents read the old single grey status line as
// "nothing happened": 9 production pulls finished and only 1 became an
// import. The popup shows real progress — contacts read of GHL's reported
// total (GhlImportSource#contacts_read / #contacts_total) — and ends on the
// result and what to do next.
export const GHL_PROGRESS_WAIT_REASON = "Wait for your contacts to finish"
export const GHL_PROGRESS_TITLES = {
  connecting: "Connecting to GHL",
  pulling: "Reading your contacts from GHL",
  finishing: "Preparing your file",
  uploading: "Uploading your GHL export",
  ready: "Your GHL contacts are ready",
  failed: "The GHL import stopped"
}
export const GHL_PROGRESS_WORKING_HINT =
  "This can take a few minutes for a large account. Nothing in GHL changes, and you can close this window — the copy keeps going."
export const GHL_PROGRESS_UPLOAD_HINT = "Keep this page open until the upload finishes."
export const GHL_PROGRESS_FAILED_HINT = "Nothing was imported."
export const GHL_IN_PROGRESS_STATES = ["connecting", "pulling", "finishing", "uploading"]

// Saved mappings (#19586), in the owner-approved mockup's words.
export const MAPPING_NAME_REASON = "Enter a mapping name"
export const RENAME_REASON = "Change a field first"
export const SAVE_MAPPING_FAILED = "The mapping could not be saved. Check your connection and try again."
export const DELETE_MAPPING_FAILED = "The mapping could not be deleted. Check your connection and try again."
export const SAVE_MAPPING_NEW_HINT =
  "Next time you import a file with these columns, every column is filled in for you. You can rename or delete it later."
export const SAVE_MAPPING_CONTINUE = "continue"
export const SAVE_MAPPING_BUTTON = "button"
// The Upload settings Continue needs, in page order: [draft key, label, target].
// Cost per Lead is optional: it is listed only when what was typed is invalid.
const UPLOAD_SETTINGS = [
  ["orderName", "Order Name", "orderName"],
  ["leadType", "Lead Type", "leadType"],
  ["costPerLead", "Cost per Lead", "costPerLead"],
  ["vendorId", "Vendor", "vendor"]
]

export function formatCount(value) {
  return Number(value || 0).toLocaleString("en-US")
}

// Where a polled pull stands, from the server's preview payload. The bar is
// indeterminate (percent null) until GHL has answered with its total, and
// full while the read contacts are written into the file.
export function ghlPullProgress(payload) {
  if (payload?.status === "ready") return { state: "ready", percent: 100 }
  if (payload?.status === "failed") return { state: "failed", percent: null }
  const total = Number(payload?.contacts_total) || 0
  const read = Number(payload?.contacts_read) || 0
  if (total <= 0) return { state: "connecting", percent: null, read, total }
  if (read >= total) return { state: "finishing", percent: 100, read, total }
  return { state: "pulling", percent: Math.min(99, Math.floor((read / total) * 100)), read, total }
}

export function listLabels(labels) {
  if (labels.length <= 1) return labels.join("")
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`
}
const REPLACEMENT_CHAR = "\uFFFD"
const NAME_SEPARATOR = " — "

// Undialable-number preview (owner ruling 2026-09-26, #14450): the review
// step says how many of the file's phone numbers cannot be dialed and badges
// the previewed rows, judged by the server's Phone::Dialability — the same
// detector the marketplace uses — and never blocks the import. The file is
// never uploaded, so the upload step keeps the full values of every column
// that LOOKS like phone numbers (header alias or mostly 10+ digit cells),
// capped at the server's batch limit, and the review step sends the one the
// agent mapped to Phone.
export const PHONE_CHECK_URL = "/api/v1/react/leads/phone_check_batch"
export const PHONE_CHECK_ROW_LIMIT = 5000 // = Api::V1::React::LeadsController::PHONE_CHECK_BATCH_LIMIT
const PHONE_LIKE_MIN_DIGITS = 10
const PHONE_LIKE_SHARE = 0.5
export const PHONE_CHECK_PENDING = "Checking the numbers in your file…"
export const PHONE_CHECK_FAILED = "The numbers could not be checked right now. They will still be imported."
export const PHONE_CHECK_STALE = "Not checked — go back to Upload and choose the file again to check its numbers."
export const PHONE_CHECK_NOT_PHONE_LIKE =
  "Not checked — the column mapped to Phone does not look like phone numbers. Its rows will still be imported."
export const PHONE_CHECK_UNMAPPED = "Map a Phone column to check its numbers."

// Required fields (owner rulings 2026-10-13, #17079): a lead needs First Name,
// Last Name, Phone and State. Lead Type is optional since owner ruling
// 2026-10-20 (#18350): none chosen and no cell → General Life, and the Upload
// choice starts on General Life. Which Lead columns are required is the
// server's LeadImports::RequiredFields, read here ONLY through each target's
// `required` flag (MappingTargets) — never a list of its own. A Contact Name column (= MappingTargets::
// CONTACT_NAME_TARGET) stands in for First AND Last Name — the server splits
// it (LeadImports::RequiredFields). Lead Type is the one chosen on Upload, or
// each row's own from a column mapped to Lead Type (blank cell → Upload's).
// A Tags column (#18350) adds each comma-separated tag in its cell.
export const CONTACT_NAME = "contact_name"
export const LEAD_TYPE_COLUMN = "lead_type"
export const TAGS_COLUMN = "tags"
export const NAME_FIELDS = ["first_name", "last_name"]
// The Review step's row count (import_leads#required_check): a CSV sends only
// the columns mapped to a required target, Contact Name or Lead Type
// (requiredCheckTargets); a GHL file is read on the server.
export const REQUIRED_CHECK_PENDING = "Counting the rows that have every required field…"
export const REQUIRED_CHECK_FAILED = "The rows could not be counted right now. Rows missing a required field are listed on the Results step."
export const REQUIRED_CHECK_STALE = "Not counted — go back to Upload and choose the file again to count its rows."

export function looksLikePhoneColumn(header, values) {
  if (autoMatchHeader(header) === "phone") return true
  const filled = values.filter((value) => value !== "")
  if (filled.length === 0) return false
  const phoneLike = filled.filter((value) => value.replace(/\D/g, "").length >= PHONE_LIKE_MIN_DIGITS).length
  return phoneLike / filled.length >= PHONE_LIKE_SHARE
}

// Header spellings that auto-match a destination field. Keys are the
// destination values the server offers (LeadImports::MappingTargets: the
// contact fields plus Contact Name, Lead Type and Tags);
// candidates are compared after lowercasing and stripping non-alphanumerics,
// so "First Name", "first_name" and "FIRSTNAME" all land on first_name.
export const HEADER_ALIASES = {
  first_name: ["firstname", "first", "fname", "givenname"],
  last_name: ["lastname", "last", "lname", "surname", "familyname"],
  contact_name: ["contactname", "fullname", "name", "leadname", "customername"],
  lead_type: ["leadtype", "producttype", "insurancetype"],
  tags: ["tags", "tag", "labels"],
  phone: ["phone", "phonenumber", "mobile", "cell", "cellphone", "telephone", "primaryphone"],
  state: ["state", "st", "stateprovince", "homestate"],
  email: ["email", "emailaddress", "mail"],
  dob: ["dob", "dateofbirth", "birthdate", "birthday", "born"],
  age: ["age", "ageyears", "currentage"],
  address: ["address", "street", "streetaddress", "address1"],
  city: ["city", "town"],
  zip: ["zip", "zipcode", "postalcode", "postal"]
}

export function normalizeHeader(header) {
  return String(header || "").toLowerCase().replace(/[^a-z0-9]/g, "")
}

export function autoMatchHeader(header) {
  const key = normalizeHeader(header)
  if (!key) return ""
  return Object.keys(HEADER_ALIASES).find((target) =>
    normalizeHeader(target) === key || HEADER_ALIASES[target].includes(key)
  ) || ""
}

// Minimal RFC 4180 reader: quoted fields, doubled quotes, CR/LF/CRLF rows.
// Returns { headers, rows } where rows excludes the header and any row whose
// every cell is blank. Deliberately small — it exists to PREVIEW a file, the
// server-side parser (#4799) is the one that validates for real.
export function parseCsv(text) {
  const records = []
  let record = []
  let field = ""
  let quoted = false
  const source = String(text || "").replace(/^\uFEFF/, "")

  for (let i = 0; i < source.length; i++) {
    const char = source[i]
    if (quoted) {
      if (char === '"') {
        if (source[i + 1] === '"') { field += '"'; i++ } else { quoted = false }
      } else {
        field += char
      }
    } else if (char === '"') {
      quoted = true
    } else if (char === ",") {
      record.push(field); field = ""
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && source[i + 1] === "\n") i++
      record.push(field); field = ""
      records.push(record); record = []
    } else {
      field += char
    }
  }
  if (field !== "" || record.length > 0) { record.push(field); records.push(record) }

  const trimmed = records.map((cells) => cells.map((cell) => cell.trim()))
  const headers = (trimmed.shift() || []).map((h) => h)
  const rows = trimmed.filter((cells) => cells.some((cell) => cell !== ""))
  return { headers, rows }
}

export function humanFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return ""
  if (bytes < 1024) return `${bytes} B`
  let value = bytes
  let unit = "B"
  for (const next of ["KB", "MB", "GB"]) {
    if (value < 1024) break
    value /= 1024
    unit = next
  }
  return `${value >= 10 ? Math.round(value) : Math.round(value * 10) / 10} ${unit}`
}

export default class extends Controller {
  static targets = [
    "fileInput", "fileError", "fileErrorText", "fileSummary",
    "sourceRadio", "csvSource", "ghlSource", "ghlFileInput",
    "ghlProgressModal", "ghlProgress", "ghlProgressTitle", "ghlProgressBar", "ghlProgressFill",
    "ghlProgressCount", "ghlProgressPercent", "ghlProgressHint", "ghlProgressAction",
    "ghlReady", "ghlReadyText", "ghlShowProgress",
    "ghlConnected", "ghlNotConnected", "ghlNotConnectedReason", "ghlConnectControl",
    "orderName", "leadType", "costPerLead", "vendor", "mode", "matchBy", "modeHint", "continueButton",
    "emptyState", "previewNote", "mappingSummary", "skippedHint", "requiredList", "blankHint", "blankHeader",
    "mappingTable", "mappingBody", "rowTemplate", "mappingCards", "cardTemplate", "ignoreAllTemplate",
    "savedNotice", "savedNoticeTitle", "savedNoticeText", "savedNoticeList", "savedUndoneHint", "savedRow",
    "savedPickerField", "savedPicker", "saveMappingButton", "manageMappingsButton", "savedEmptyHint", "savedFlash",
    "saveMappingModal", "saveMappingQuestion", "saveMappingError", "saveMappingErrorText", "saveMappingName",
    "saveMappingHint", "saveMappingReason", "saveMappingSecondary", "saveMappingSubmit", "saveMappingSubmitLabel",
    "manageMappingsModal", "manageMappingsError", "manageMappingsErrorText", "manageMappingsBody",
    "manageMappingsCards", "manageRowTemplate", "manageCardTemplate",
    "reviewSummary", "reviewMappingBody", "reviewMappingCards", "reviewCardTemplate",
    "tagsCheckbox", "tagsPicker", "workflowCheckbox", "workflowSelect",
    "tcpaCheckbox", "submitButton", "submitReason", "submitErrors", "submitErrorList"
  ]

  static values = {
    step: String,
    nextUrl: String,
    createUrl: String,
    maxBytes: { type: Number, default: 0 },
    maxRows: { type: Number, default: 0 },
    maxCost: { type: Number, default: 0 },
    targets: { type: Array, default: [] },
    modes: { type: Array, default: [] },
    ghlSourcesUrl: String,
    ghlSourceId: String,
    ghlConnectUrl: String,
    ghlConnectFailed: { type: Boolean, default: false },
    requiredCheckUrl: String,
    savedMappings: { type: Array, default: [] },
    mappingsUrl: String,
    mappingsMatchUrl: String,
    timeZone: String
  }

  connect () {
    this.draft = this.loadDraft()
    this.render()
  }

  disconnect () {
    this.stopGhlPoll()
  }

  // ── Step dispatch ─────────────────────────────────────────────────────────

  render () {
    switch (this.stepValue) {
      case "upload": this.renderUpload(); break
      case "map": this.renderMap(); break
      case "verify": this.renderReview(); break
      default: break
    }
  }

  // ── Upload ────────────────────────────────────────────────────────────────

  renderUpload () {
    if (this.hasOrderNameTarget && this.draft.orderName) this.orderNameTarget.value = this.draft.orderName
    if (this.hasLeadTypeTarget && this.draft.leadType) this.leadTypeTarget.value = this.draft.leadType
    // The select starts on General Life (#18350): a fresh draft takes it.
    if (this.hasLeadTypeTarget && !this.draft.leadType && this.leadTypeTarget.value) {
      this.readLeadType()
      this.saveDraft()
    }
    if (this.hasCostPerLeadTarget && this.draft.costPerLead) this.costPerLeadTarget.value = this.draft.costPerLead
    if (this.hasVendorTarget && this.draft.vendorId) this.vendorTarget.value = this.draft.vendorId
    if (this.hasModeTarget && this.draft.mode) this.modeTarget.value = this.draft.mode
    if (this.hasMatchByTarget && this.draft.matchBy) this.matchByTarget.value = this.draft.matchBy
    this.syncModeHint()
    // Back from Connect GHL: wait on that pull (it replaces any earlier file).
    if (this.ghlSourceIdValue) {
      this.draft.source = GHL_SOURCE
      this.saveDraft()
    }
    // Back from a Connect GHL that failed (#17672): open the GHL option so its
    // Not Connected status and Connect Again are in view. A CSV chosen under
    // the other option is dropped, as switching source by hand does.
    const backFromFailedConnect = this.ghlConnectFailedValue && !this.ghlSourceIdValue
    if (backFromFailedConnect) {
      this.draft.source = GHL_SOURCE
      this.saveDraft()
    }
    this.renderSource()
    this.renderFileSummary()
    this.gateUploadContinue()
    if (backFromFailedConnect && this.draft.file && !this.draft.file.ghlSourceId) this.setFile(null)
    if (this.ghlSourceIdValue && this.draft.file?.ghlSourceId !== this.ghlSourceIdValue) {
      this.setFile(null)
      this.ghlSourceKind = "api"
      this.renderGhlProgress("connecting")
      this.openGhlProgress()
      this.pollGhlSource(this.ghlSourceIdValue)
    }
  }

  // ── Import from GHL (#15701) ──────────────────────────────────────────────

  currentSource () {
    return this.draft.source === GHL_SOURCE ? GHL_SOURCE : CSV_SOURCE
  }

  renderSource () {
    const source = this.currentSource()
    this.sourceRadioTargets.forEach((radio) => { radio.checked = radio.value === source })
    if (this.hasCsvSourceTarget) this.csvSourceTarget.hidden = source !== CSV_SOURCE
    if (this.hasGhlSourceTarget) this.ghlSourceTarget.hidden = source !== GHL_SOURCE
  }

  // Switching source forgets a file chosen from the other one, so the draft
  // never carries a CSV under "Import from GHL" or the other way round.
  sourceChanged (event) {
    const source = event?.currentTarget?.value === GHL_SOURCE ? GHL_SOURCE : CSV_SOURCE
    if (source === this.currentSource()) return
    this.draft.source = source
    this.stopGhlPoll()
    // Nothing is tracked any more, so the "View Progress" button goes too —
    // otherwise switching back would reopen a popup frozen on a stale pull.
    this.ghlProgressState = null
    this.renderGhlShowProgress()
    this.hideFileError()
    this.rejectedFileName = null
    this.setFile(null)
    this.renderSource()
  }

  async ghlFileChanged () {
    const file = this.hasGhlFileInputTarget ? this.ghlFileInputTarget.files?.[0] : null
    this.hideFileError()
    if (!file) return
    if (!/\.csv$/i.test(file.name) && !/csv/i.test(file.type || "")) {
      this.rejectFile(file, `Choose the .csv file GHL exported — “${file.name}” is not one.`)
      return
    }
    this.setFile(null)
    this.showStatus(GHL_UPLOADING)
    this.ghlSourceKind = "export"
    this.renderGhlProgress("uploading", { count: [file.name, humanFileSize(file.size)].filter(Boolean).join(" · ") })
    this.openGhlProgress()
    const form = new FormData()
    form.append("csv_file", file, file.name)
    try {
      const response = await post(this.ghlSourcesUrlValue, { body: form, responseKind: "json" })
      const payload = await this.responseJson(response)
      if (response.ok && payload) this.acceptGhlSource(payload)
      else this.failGhl(file, payload?.errors?.[0] || GHL_UPLOAD_FAILED)
    } catch {
      this.failGhl(file, GHL_UPLOAD_FAILED)
    }
  }

  failGhl (file, message) {
    this.rejectFile(file, message)
    this.renderGhlProgress("failed", { count: message })
  }

  async pollGhlSource (id) {
    this.stopGhlPoll()
    this.showStatus(GHL_PULLING)
    let payload = null
    try {
      const response = await get(`${this.ghlSourcesUrlValue}/${encodeURIComponent(id)}`, { responseKind: "json" })
      payload = response.ok ? await this.responseJson(response) : null
    } catch {
      payload = null
    }
    if (payload) this.renderGhlConnection(payload.connection)
    if (!payload) {
      this.showFileError(GHL_PULL_FAILED)
      this.renderFileSummary()
      this.renderGhlProgress("failed", { count: GHL_PULL_FAILED })
      // Reopened like the failed-status branch: the agent may have closed it.
      this.openGhlProgress()
      return
    }
    if (payload.status === "ready") {
      this.acceptGhlSource(payload)
    } else if (payload.status === "failed") {
      this.showFileError(payload.error || GHL_PULL_FAILED)
      this.renderFileSummary()
      this.renderGhlProgress("failed", { count: payload.error || GHL_PULL_FAILED })
      this.openGhlProgress()
    } else {
      const { state, percent, read, total } = ghlPullProgress(payload)
      const count = state === "pulling"
        ? `${formatCount(read)} of ${formatCount(total)} contacts read`
        : state === "finishing" ? `All ${formatCount(total)} contacts read — building your file…` : undefined
      this.renderGhlProgress(state, { percent, count })
      this.ghlPollTimer = setTimeout(() => this.pollGhlSource(id), GHL_POLL_MS)
    }
  }

  stopGhlPoll () {
    if (this.ghlPollTimer) clearTimeout(this.ghlPollTimer)
    this.ghlPollTimer = null
  }

  // The server's preview has the same shape the browser builds for a CSV.
  acceptGhlSource (preview) {
    this.hideFileError()
    this.setFile({
      name: preview.name,
      size: preview.size,
      headers: preview.headers || [],
      samples: preview.samples || [],
      phoneColumns: preview.phone_columns || {},
      rowCount: preview.row_count || 0,
      ghlSourceId: String(preview.id)
    })
    this.renderGhlProgress("ready")
    // Reopened even if the agent closed it while the pull ran: the moment the
    // contacts are ready is the one the old status line let slip by.
    this.openGhlProgress()
  }

  // ── Connected to GHL status (#17672) ─────────────────────────────────────
  // The page renders it from the record; the poll only ever turns it red —
  // when GHL ended the grant before the contacts were copied.
  renderGhlConnection (connection) {
    if (connection?.state !== "not_connected") return
    if (this.hasGhlConnectedTarget) this.ghlConnectedTarget.hidden = true
    if (this.hasGhlNotConnectedTarget) this.ghlNotConnectedTarget.hidden = false
    if (this.hasGhlConnectControlTarget) this.ghlConnectControlTarget.hidden = true
    this.setText("ghlNotConnectedReason", connection.reason || "")
  }

  // ── GHL progress popup (#17615) ───────────────────────────────────────────

  renderGhlProgress (state, { percent = null, count } = {}) {
    this.ghlProgressState = state
    this.renderGhlShowProgress()
    if (!this.hasGhlProgressTarget) return
    const working = GHL_IN_PROGRESS_STATES.includes(state)
    // "finishing" reads 100% of contacts but the file is still being written:
    // the bar slides, so no number contradicts it.
    const shown = state === "ready" ? 100 : (state === "finishing" ? null : percent)
    this.ghlProgressTarget.dataset.state = state
    this.setText("ghlProgressTitle", GHL_PROGRESS_TITLES[state] || "")
    const countText = count ?? this.ghlProgressCountText(state)
    this.setText("ghlProgressCount", countText)
    this.setText("ghlProgressPercent", shown === null || state === "failed" ? "" : `${shown}%`)
    this.setText("ghlProgressHint", this.ghlProgressHintText(state))
    if (this.hasGhlProgressFillTarget) {
      const indeterminate = working && shown === null
      this.ghlProgressFillTarget.classList.toggle("gl-wizard-progress-fill--indeterminate", indeterminate)
      this.ghlProgressFillTarget.style.width = indeterminate || state === "failed" ? "" : `${shown}%`
    }
    if (this.hasGhlProgressBarTarget) {
      const bar = this.ghlProgressBarTarget
      if (shown === null || state === "failed") bar.removeAttribute("aria-valuenow")
      else bar.setAttribute("aria-valuenow", String(shown))
      bar.setAttribute("aria-valuetext", countText || GHL_PROGRESS_TITLES[state] || "")
    }
    this.renderGhlProgressAction()
  }

  ghlProgressCountText (state) {
    const file = this.draft.file
    if (state === "ready" && file) {
      const columns = `${file.headers.length} ${file.headers.length === 1 ? "column" : "columns"}`
      return `${formatCount(file.rowCount)} ${file.rowCount === 1 ? "contact" : "contacts"} · ${columns}`
    }
    if (state === "connecting") return "Getting your contacts ready to read…"
    return ""
  }

  ghlProgressHintText (state) {
    if (state === "uploading") return GHL_PROGRESS_UPLOAD_HINT
    if (state === "failed") return GHL_PROGRESS_FAILED_HINT
    if (state !== "ready") return GHL_PROGRESS_WORKING_HINT
    const missing = this.missingUploadSettings().map(([, label]) => label)
    return missing.length
      ? `Next: fill in ${listLabels(missing)}, then press Continue to match your GHL columns to GOAT fields.`
      : "Next: match your GHL columns to GOAT fields."
  }

  renderGhlProgressAction () {
    if (!this.hasGhlProgressActionTarget) return
    const button = this.ghlProgressActionTarget
    const state = this.ghlProgressState
    let label = "Continue"
    if (state === "ready") label = this.uploadComplete() ? "Continue to Match Fields" : "Fill In Settings"
    else if (state === "failed") label = this.ghlSourceKind === "export" ? "Choose Another File" : "Connect GHL Again"
    button.textContent = label
    button.dataset.importWizardLabel = label
    if (state === "ready" || state === "failed") this.enable(button)
    else this.disable(button, GHL_PROGRESS_WAIT_REASON)
  }

  // "View Progress" while a pull runs; the green banner once the file is ready.
  renderGhlShowProgress () {
    if (this.hasGhlShowProgressTarget) {
      this.ghlShowProgressTarget.hidden = !GHL_IN_PROGRESS_STATES.includes(this.ghlProgressState)
    }
  }

  renderGhlReady () {
    if (!this.hasGhlReadyTarget) return
    const file = this.draft.file
    const ready = Boolean(file?.ghlSourceId) && this.currentSource() === GHL_SOURCE
    this.ghlReadyTarget.hidden = !ready
    if (ready && this.hasGhlReadyTextTarget) {
      this.ghlReadyTextTarget.textContent = `${this.ghlProgressCountText("ready")} · ${file.name}`
    }
  }

  ghlProgressContinue (event) {
    if (event?.currentTarget?.disabled) return
    if (this.ghlProgressState === "failed") {
      this.hideGhlProgress()
      if (this.ghlSourceKind === "export") this.optionalTarget("ghlFileInput")?.click()
      else if (this.ghlConnectUrlValue) window.location.assign(this.ghlConnectUrlValue)
      return
    }
    if (this.ghlProgressState !== "ready") return
    this.hideGhlProgress()
    if (this.uploadComplete()) { this.continueToNext(); return }
    const [first] = this.missingUploadSettings()
    const field = first ? this.optionalTarget(first[2]) : null
    if (field) {
      field.scrollIntoView?.({ block: "center", behavior: "smooth" })
      field.focus?.()
    }
  }

  showGhlProgress () {
    this.openGhlProgress()
  }

  openGhlProgress () {
    if (this.hasGhlProgressModalTarget) Modal.getOrCreateInstance(this.ghlProgressModalTarget).show()
  }

  hideGhlProgress () {
    if (this.hasGhlProgressModalTarget) Modal.getOrCreateInstance(this.ghlProgressModalTarget).hide()
  }

  missingUploadSettings () {
    return UPLOAD_SETTINGS.filter(([key]) => {
      if (key === "costPerLead") return !this.validCost()
      return !String(this.draft[key] ?? "").trim()
    })
  }

  // A target by name, or null when this step's markup does not carry it.
  optionalTarget (name) {
    return this[`has${name[0].toUpperCase()}${name.slice(1)}Target`] ? this[`${name}Target`] : null
  }

  setText (name, text) {
    const target = this.optionalTarget(name)
    if (target) target.textContent = text
  }

  showStatus (message) {
    if (this.hasFileSummaryTarget) this.fileSummaryTarget.textContent = message
  }

  fileChanged () {
    const file = this.hasFileInputTarget ? this.fileInputTarget.files?.[0] : null
    this.hideFileError()
    if (!file) {
      this.setFile(null)
      return
    }
    if (!/\.csv$/i.test(file.name) && !/csv/i.test(file.type || "")) {
      this.rejectFile(file, `Choose a .csv file — “${file.name}” is not one.`)
      return
    }
    if (this.maxBytesValue > 0 && file.size > this.maxBytesValue) {
      this.rejectFile(file, `“${file.name}” is ${humanFileSize(file.size)}; the limit is ${humanFileSize(this.maxBytesValue)}. Split it and import each part.`)
      return
    }
    this.readFile(file, "utf-8")
  }

  // UTF-8 first; a file that does not decode as UTF-8 (replacement characters)
  // is re-read as Windows-1252, the usual spreadsheet export — the same
  // fallback the server parser applies.
  readFile (file, encoding) {
    const reader = new FileReader()
    reader.onerror = () => this.showFileError("The file could not be read. Try choosing it again.")
    reader.onload = () => {
      const text = String(reader.result || "")
      if (encoding === "utf-8" && text.includes(REPLACEMENT_CHAR)) this.readFile(file, "windows-1252")
      else this.acceptCsv(file, text)
    }
    reader.readAsText(file, encoding)
  }

  acceptCsv (file, text) {
    const { headers, rows } = parseCsv(text)
    const namedHeaders = headers.filter((header) => header !== "")
    if (namedHeaders.length === 0) {
      this.rejectFile(file, "The first row must be a header row naming each column.")
      return
    }
    if (rows.length === 0) {
      this.rejectFile(file, "This file has a header row but no leads under it.")
      return
    }
    if (this.maxRowsValue > 0 && rows.length > this.maxRowsValue) {
      this.rejectFile(file, `This file has ${rows.length} rows; the limit is ${this.maxRowsValue}. Split it and import each part.`)
      return
    }
    if (!this.saveCsvText(text)) {
      this.rejectFile(file, "This browser could not hold the file for the import. Close other tabs of this site, or split the file, and try again.")
      return
    }
    const samples = headers.map((_header, index) =>
      rows.slice(0, SAMPLE_ROWS).map((row) => row[index] ?? "").filter((value) => value !== "")
    )
    const phoneColumns = {}
    headers.forEach((header, index) => {
      const values = rows.slice(0, PHONE_CHECK_ROW_LIMIT).map((row) => row[index] ?? "")
      if (looksLikePhoneColumn(header, values)) phoneColumns[index] = values
    })
    this.setFile({ name: file.name, size: file.size, headers, samples, phoneColumns, rowCount: rows.length })
  }

  // A picked file that cannot be previewed: the draft forgets it, the shared
  // chip keeps showing what was picked, and the summary + alert say why it was
  // not read (so "No file chosen yet" never contradicts a visible chip).
  rejectFile (file, message) {
    this.rejectedFileName = file.name
    this.setFile(null)
    this.showFileError(message)
  }

  setFile (file) {
    if (file) this.rejectedFileName = null
    else this.clearCsvText()
    this.draft.file = file
    this.draft.mapping = file ? this.autoMapping(file.headers) : {}
    this.draft.mappedForLeadType = this.draft.leadType
    this.draft.keepBlank = {}
    this.draft.savedMapping = null
    this.draft.savedMappingChecked = false
    this.draft.savedMappingUndone = false
    this.saveDraft()
    this.renderFileSummary()
    this.gateUploadContinue()
  }

  autoMapping (headers, mapping = {}) {
    const used = new Set(Object.values(mapping).filter((value) => value && value !== IGNORE_VALUE))
    const result = { ...mapping }
    headers.forEach((header, index) => {
      const key = this.columnKey(headers, index)
      if (result[key]) return
      const target = this.matchTarget(header, used)
      result[key] = target || ""
      if (target) used.add(target)
    })
    return result
  }

  // A header matches a contact field by its aliases first, then any field
  // offered for the chosen lead type — its own type fields and the agent's
  // custom fields — by name (#15705).
  matchTarget (header, used) {
    const available = this.targetValues()
    const alias = autoMatchHeader(header)
    if (alias && !used.has(alias) && available.includes(alias)) return alias
    const key = normalizeHeader(header)
    if (!key) return ""
    const byLabel = this.availableTargets().find((target) =>
      !used.has(target.value) && normalizeHeader(target.label) === key)
    return byLabel ? byLabel.value : ""
  }

  // A choice no longer offered (another lead type's field, a custom field
  // archived since the draft was saved) is dropped on every render, so it is
  // never posted. When the lead type changed, the columns left open are also
  // matched again for the new type.
  reconcileMapping () {
    const file = this.draft.file
    if (!file) return
    const available = new Set(this.targetValues())
    const kept = Object.fromEntries(Object.entries(this.draft.mapping || {}).map(([key, value]) =>
      [key, !value || value === IGNORE_VALUE || available.has(value) ? value : ""]))
    const typeChanged = this.draft.mappedForLeadType !== this.draft.leadType
    this.draft.mapping = typeChanged ? this.autoMapping(file.headers, kept) : kept
    this.draft.mappedForLeadType = this.draft.leadType
    this.saveDraft()
  }

  readLeadType () {
    this.draft.leadType = this.leadTypeTarget.value
    const option = this.leadTypeTarget.selectedOptions?.[0]
    this.draft.leadTypeLabel = this.draft.leadType && option ? option.textContent.trim() : ""
  }

  settingChanged () {
    if (this.hasLeadTypeTarget) this.readLeadType()
    if (this.hasOrderNameTarget) this.draft.orderName = this.orderNameTarget.value
    if (this.hasCostPerLeadTarget) this.draft.costPerLead = this.costPerLeadTarget.value
    if (this.hasVendorTarget) {
      const option = this.vendorTarget.selectedOptions?.[0]
      this.draft.vendorId = this.vendorTarget.value
      this.draft.vendor = this.draft.vendorId && option ? option.textContent.trim() : ""
    }
    if (this.hasModeTarget) this.draft.mode = this.modeTarget.value
    if (this.hasMatchByTarget) this.draft.matchBy = this.matchByTarget.value
    if (this.hasWorkflowSelectTarget) {
      const option = this.workflowSelectTarget.selectedOptions?.[0]
      this.draft.workflowId = this.workflowSelectTarget.value
      this.draft.workflowName = this.draft.workflowId && option ? option.textContent.trim() : ""
    }
    this.saveDraft()
    this.syncModeHint()
    this.gateUploadContinue()
    if (this.stepValue === "verify") this.renderReviewSummary()
  }

  syncModeHint () {
    if (!this.hasModeHintTarget) return
    const mode = this.currentMode()
    const entry = this.modesValue.find((m) => m.value === mode)
    if (entry) this.modeHintTarget.textContent = entry.hint
  }

  renderFileSummary () {
    this.renderGhlReady()
    if (!this.hasFileSummaryTarget) return
    const file = this.draft.file
    if (!file) {
      this.fileSummaryTarget.textContent = this.rejectedFileName
        ? `${this.rejectedFileName} was not read. Choose a .csv file.`
        : "No file chosen yet."
      return
    }
    const size = humanFileSize(file.size)
    const columns = `${file.headers.length} ${file.headers.length === 1 ? "column" : "columns"}`
    const rows = `${file.rowCount} data ${file.rowCount === 1 ? "row" : "rows"}`
    if (file.ghlSourceId) {
      this.fileSummaryTarget.textContent =
        `${file.name}${size ? ` · ${size}` : ""} · ${columns} · ${rows} · held for this import.`
      return
    }
    const picked = this.hasFileInputTarget && this.fileInputTarget.files?.length > 0
    const prefix = picked ? "" : "Previously read: "
    this.fileSummaryTarget.textContent =
      `${prefix}${file.name}${size ? ` · ${size}` : ""} · ${columns} · ${rows} · read in your browser, not uploaded.`
  }

  gateUploadContinue () {
    if (this.ghlProgressState === "ready") {
      this.renderGhlProgressAction()
      this.setText("ghlProgressHint", this.ghlProgressHintText("ready"))
    }
    if (this.stepValue !== "upload" || !this.hasContinueButtonTarget) return
    if (this.uploadComplete()) this.enable(this.continueButtonTarget)
    else this.disable(this.continueButtonTarget, this.continueButtonTarget.dataset.importWizardDisabledReason)
  }

  continueToNext (event) {
    if (event?.currentTarget?.disabled) return
    // Map Fields (#19586): a new or changed mapping is offered for saving
    // first; an applied saved mapping left as it was goes straight on.
    if (this.stepValue === "map" && this.draft.file && this.hasSaveMappingModalTarget) {
      const saved = this.draft.savedMapping
      if (!saved || saved.dirty) {
        this.openSaveMapping(SAVE_MAPPING_CONTINUE)
        return
      }
    }
    this.saveDraft()
    this.visit(this.nextUrlValue)
  }

  showFileError (message) {
    if (!this.hasFileErrorTarget) return
    if (this.hasFileErrorTextTarget) this.fileErrorTextTarget.textContent = message
    this.fileErrorTarget.hidden = false
  }

  hideFileError () {
    if (this.hasFileErrorTarget) this.fileErrorTarget.hidden = true
  }

  // ── Map ───────────────────────────────────────────────────────────────────

  renderMap () {
    const file = this.draft.file
    const hasFile = Boolean(file)
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = hasFile
    if (this.hasPreviewNoteTarget) this.previewNoteTarget.hidden = !hasFile
    if (this.hasMappingTableTarget) this.mappingTableTarget.hidden = !hasFile
    if (!hasFile) {
      if (this.hasMappingSummaryTarget) this.mappingSummaryTarget.replaceChildren()
      if (this.hasSkippedHintTarget) this.skippedHintTarget.hidden = true
      if (this.hasRequiredListTarget) this.requiredListTarget.replaceChildren()
      if (this.hasBlankHintTarget) this.blankHintTarget.hidden = true
      if (this.hasContinueButtonTarget) this.disable(this.continueButtonTarget, NO_FILE_REASON)
      return
    }
    if (this.hasFileSummaryTarget) {
      const rows = `${file.rowCount} data ${file.rowCount === 1 ? "row" : "rows"}`
      this.fileSummaryTarget.textContent = `${file.name} · ${file.headers.length} columns · ${rows}.`
    }
    this.reconcileMapping()
    this.renderMappingRows()
    this.refreshMapState()
    if (!this.draft.savedMappingChecked) this.matchSavedMapping()
  }

  // The desktop rows and, below md, their mobile-card twins (#15175) are
  // built from the same draft by the same builder; each item carries
  // data-import-wizard-column, so every handler below addresses "the item for
  // this column" without caring which of the two views it is in.
  renderMappingRows () {
    if (!this.hasMappingBodyTarget || !this.hasRowTemplateTarget) return
    const file = this.draft.file
    const build = (template, card) => file.headers.map((_header, index) =>
      this.buildMappingRow(file.headers, index, file.samples[index] || [], template, card))
    this.mappingBodyTarget.replaceChildren(...build(this.rowTemplateTarget, false))
    if (this.hasMappingCardsTarget && this.hasCardTemplateTarget) {
      this.mappingCardsTarget.replaceChildren(...build(this.cardTemplateTarget, true))
    }
  }

  // Every mapping item (rows, then cards) — the twins refreshMapState keeps in step.
  mappingItems () {
    const containers = [
      this.hasMappingBodyTarget && this.mappingBodyTarget,
      this.hasMappingCardsTarget && this.mappingCardsTarget
    ].filter(Boolean)
    return containers.flatMap((container) => Array.from(container.querySelectorAll("[data-import-wizard-column]")))
  }

  buildMappingRow (headers, index, samples, template = this.rowTemplateTarget, card = false) {
    const row = template.content.firstElementChild.cloneNode(true)
    const key = this.columnKey(headers, index)
    const displayName = this.columnDisplayName(headers, index)
    row.dataset.importWizardColumn = key

    row.querySelector("[data-import-wizard-row='column']").textContent = displayName

    const samplesCell = row.querySelector("[data-import-wizard-row='samples']")
    if (samples.length === 0) {
      setValueOrEmpty(samplesCell, null)
    } else {
      samplesCell.replaceChildren(...samples.map((value) => {
        const line = document.createElement("div")
        line.textContent = value
        return line
      }))
    }

    // A card's controls get their own ids and NO name: the row's controls
    // already name each column, and a duplicate name would post twice.
    const idPrefix = card ? "import_card_" : "import_"
    const select = row.querySelector("[data-import-wizard-row='field']")
    select.id = `${idPrefix}mapping_${index}`
    if (card) select.removeAttribute("name")
    else select.name = `mapping[${key}]`
    select.setAttribute("aria-label", `Field for column ${displayName}`)
    this.pruneLeadTypeOptions(select)
    select.value = this.draft.mapping[key] ?? ""
    if (select.value !== (this.draft.mapping[key] ?? "")) select.value = ""

    const checkbox = row.querySelector("[data-import-wizard-row='keepBlank']")
    const label = row.querySelector("[data-import-wizard-row='keepBlankLabel']")
    checkbox.id = `${idPrefix}keep_blank_${index}`
    if (card) checkbox.removeAttribute("name")
    else checkbox.name = `keep_blank[${key}]`
    checkbox.checked = Boolean(this.draft.keepBlank[key])
    checkbox.setAttribute("aria-label", `${label.textContent.trim()} for column ${displayName}`)
    label.setAttribute("for", checkbox.id)
    return row
  }

  // Only the chosen lead type's own fields are offered; an emptied group goes.
  pruneLeadTypeOptions (select) {
    select.querySelectorAll("option[data-lead-class]").forEach((option) => {
      if (option.dataset.leadClass !== this.draft.leadType) option.remove()
    })
    select.querySelectorAll("optgroup").forEach((group) => {
      if (!group.querySelector("option")) group.remove()
    })
  }

  mappingChanged (event) {
    const select = event.currentTarget
    const key = select.closest("[data-import-wizard-column]")?.dataset.importWizardColumn
    if (key === undefined) return
    if (select.value === CREATE_CUSTOM_VALUE) {
      select.value = this.draft.mapping[key] ?? ""
      this.createCustomField(key)
      return
    }
    this.draft.mapping[key] = select.value
    this.markMappingEdited()
    this.saveDraft()
    this.refreshMapState()
  }

  // Inline "Create custom field" (#15705): opens the shared Add Custom Field
  // popup (#19714) with the column's header as the name. The new field joins
  // every dropdown (templates included, for rows built later) and this column
  // maps to it. Cancelled, the column keeps its previous choice.
  createCustomField (key) {
    const headers = this.draft.file?.headers || []
    const index = headers.findIndex((_header, i) => this.columnKey(headers, i) === key)
    this.pendingCustomFieldKey = key
    this.addFieldPopup.open({ name: index >= 0 ? this.columnDisplayName(headers, index) : "" })
  }

  get addFieldPopup () {
    this._addFieldPopup ||= new AddCustomFieldIsland()
    return this._addFieldPopup
  }

  // add-custom-field:ready — the popup island is listening.
  addFieldReady (event) {
    this.addFieldPopup.ready(event.currentTarget)
  }

  // add-custom-field:created — the create's answer carries the mapping target.
  customFieldCreated (event) {
    const target = event.detail?.definition?.target
    const key = this.pendingCustomFieldKey
    if (!target || key === undefined) return
    this.pendingCustomFieldKey = undefined
    this.targetsValue = [...this.targetsValue, target]
    this.addTargetOption(target)
    this.draft.mapping[key] = target.value
    this.markMappingEdited()
    this.saveDraft()
    this.refreshMapState()
  }

  addTargetOption (target) {
    const selects = [
      ...[this.hasRowTemplateTarget && this.rowTemplateTarget, this.hasCardTemplateTarget && this.cardTemplateTarget]
        .filter(Boolean)
        .flatMap((template) => Array.from(template.content.querySelectorAll("[data-import-wizard-row='field']"))),
      ...this.mappingItems().map((item) => item.querySelector("[data-import-wizard-row='field']")).filter(Boolean)
    ]
    selects.forEach((select) => {
      const create = select.querySelector(`option[value='${CREATE_CUSTOM_VALUE}']`)
      const option = document.createElement("option")
      option.value = target.value
      option.textContent = target.label
      if (create) create.before(option)
      else select.append(option)
    })
  }

  // "Ignore All N Unmapped" (#16949): every column still on "Please select"
  // becomes "Ignore this column" in the draft; mapped and already-ignored
  // columns are left alone. The same refresh as a manual change then re-syncs
  // both twins (row and card) and every badge, and the counts row says how
  // many were set.
  ignoreAllUnmapped () {
    const mapping = this.draft.mapping || {}
    const unmapped = Object.keys(mapping).filter((key) => !mapping[key])
    if (unmapped.length === 0) return
    unmapped.forEach((key) => { mapping[key] = IGNORE_VALUE })
    this.draft.mapping = mapping
    this.markMappingEdited()
    this.saveDraft()
    this.refreshMapState({ justIgnored: unmapped.length })
  }

  keepBlankChanged (event) {
    const key = event.currentTarget.closest("[data-import-wizard-column]")?.dataset.importWizardColumn
    if (key === undefined) return
    this.draft.keepBlank[key] = event.currentTarget.checked
    this.saveDraft()
    this.syncTwins()
  }

  // Write the draft back into every item's controls, so the twin of the
  // control the user just changed (row and card) shows the same choice.
  syncTwins () {
    this.mappingItems().forEach((item) => {
      const key = item.dataset.importWizardColumn
      const select = item.querySelector("[data-import-wizard-row='field']")
      if (select) select.value = this.draft.mapping?.[key] ?? ""
      const checkbox = item.querySelector("[data-import-wizard-row='keepBlank']")
      if (checkbox) checkbox.checked = Boolean(this.draft.keepBlank?.[key])
    })
  }

  // Statuses, counts, required checklist and the Continue gate — derived from
  // the draft every time, never asserted.
  // justIgnored: how many columns an "Ignore All" click just set — shown
  // as a hint until the next refresh.
  refreshMapState ({ justIgnored = 0 } = {}) {
    const stats = this.mappingStats()
    this.syncTwins()
    this.mappingItems().forEach((row) => {
      const key = row.dataset.importWizardColumn
      const cell = row.querySelector("[data-import-wizard-row='status']")
      if (cell) cell.replaceChildren(this.statusBadge(this.columnStatus(key, stats)))
    })
    this.renderMappingSummary(stats, justIgnored)
    this.renderRequiredList(stats)
    this.toggleBlankColumn()
    if (this.hasContinueButtonTarget) {
      const reason = this.mappingBlocker(stats)
      if (reason) this.disable(this.continueButtonTarget, reason)
      else this.enable(this.continueButtonTarget)
    }
    this.renderSavedMapping()
  }

  mappingStats () {
    const mapping = this.draft.mapping || {}
    const values = Object.values(mapping)
    const counts = {}
    values.forEach((value) => {
      if (value && value !== IGNORE_VALUE) counts[value] = (counts[value] || 0) + 1
    })
    const duplicates = Object.keys(counts).filter((value) => counts[value] > 1)
    const mapped = values.filter((value) => value && value !== IGNORE_VALUE).length
    const ignored = values.filter((value) => value === IGNORE_VALUE).length
    const unmapped = values.filter((value) => !value).length
    const missingRequired = this.requiredTargets().filter((target) => !this.requirementMet(target, counts))
    return { counts, duplicates, mapped, ignored, unmapped, missingRequired }
  }

  // The required contact fields (the targets' `required` flag, in every
  // mode), plus Email when an update mode matches existing leads by email
  // (#16803). Lead Type is never required (#18350).
  requiredTargets () {
    const required = this.targetsValue.filter((target) => target.required)
    const matchingByEmail = UPDATE_MODES.includes(this.currentMode()) && this.currentMatchBy() === "email"
    const email = this.targetsValue.find((target) => target.value === "email")
    return matchingByEmail && email && !email.required ? [...required, email] : required
  }

  // A First / Last Name is met by its own column or by a Contact Name column.
  requirementMet (target, counts) {
    if (NAME_FIELDS.includes(target.value) && counts[CONTACT_NAME]) return true
    return Boolean(counts[target.value])
  }

  // How a missing requirement reads in a reason: what can satisfy it.
  requirementLabel (target) {
    if (NAME_FIELDS.includes(target.value)) return `${target.label} (or Contact Name)`
    return target.label
  }

  columnStatus (key, stats) {
    const value = this.draft.mapping?.[key] ?? ""
    if (value === IGNORE_VALUE) return { label: "Ignored", tone: "secondary", icon: "ph-minus-circle" }
    if (!value) return { label: "Not mapped", tone: "warning", icon: "ph-circle-dashed" }
    if (stats.duplicates.includes(value)) return { label: "Duplicate", tone: "danger", icon: "ph-warning-circle" }
    return { label: "Mapped", tone: "success", icon: "ph-check-circle" }
  }

  mappingBlocker (stats) {
    if (stats.duplicates.length > 0) return DUPLICATE_REASON
    if (stats.missingRequired.length > 0) {
      return `${REQUIRED_REASON}: ${stats.missingRequired.map((t) => this.requirementLabel(t)).join(", ")}`
    }
    return null
  }

  renderMappingSummary (stats, justIgnored = 0) {
    if (!this.hasMappingSummaryTarget) return
    const badges = [
      this.statusBadge({ label: `${stats.mapped} mapped`, tone: "success", icon: "ph-check-circle" }),
      this.statusBadge({ label: `${stats.ignored} ignored`, tone: "secondary", icon: "ph-minus-circle" }),
      this.statusBadge({ label: `${stats.unmapped} not mapped`, tone: stats.unmapped > 0 ? "warning" : "secondary", icon: "ph-circle-dashed" })
    ]
    if (stats.duplicates.length > 0) {
      badges.push(this.statusBadge({ label: `${stats.duplicates.length} duplicate`, tone: "danger", icon: "ph-warning-circle" }))
    }
    const ignoreAll = this.ignoreAllItem(stats.unmapped, justIgnored)
    if (ignoreAll) badges.push(ignoreAll)
    this.mappingSummaryTarget.replaceChildren(...badges)
    // "Columns that are not mapped are skipped" (#16949 item 3): only while
    // the "N not mapped" badge is above 0. Continue stays allowed.
    if (this.hasSkippedHintTarget) this.skippedHintTarget.hidden = stats.unmapped === 0
  }

  // The button while any column is unmapped; after a click that left none,
  // the hint saying how to undo one column. Both are authored in the view.
  ignoreAllItem (unmapped, justIgnored) {
    if (!this.hasIgnoreAllTemplateTarget) return null
    const template = this.ignoreAllTemplateTarget.content
    if (unmapped > 0) {
      const button = template.querySelector("[data-import-wizard-row='ignoreAll']").cloneNode(true)
      button.querySelector("[data-import-wizard-row='ignoreAllLabel']").textContent = `Ignore All ${unmapped} Unmapped`
      return button
    }
    if (justIgnored === 0) return null
    const hint = template.querySelector("[data-import-wizard-row='ignoreAllHint']").cloneNode(true)
    const noun = justIgnored === 1 ? "column" : "columns"
    hint.textContent = `${justIgnored} ${noun} set to Ignore. Pick a field on any row to map it instead.`
    return hint
  }

  renderRequiredList (stats) {
    if (!this.hasRequiredListTarget) return
    const missing = new Set(stats.missingRequired.map((t) => t.value))
    const fromContactName = Boolean(stats.counts[CONTACT_NAME])
    const intro = document.createElement("span")
    intro.textContent = "Required:"
    const items = this.requiredTargets().map((target) => {
      const item = document.createElement("span")
      const icon = document.createElement("i")
      const ok = !missing.has(target.value)
      icon.className = `ph ${ok ? "ph-check" : "ph-x"} me-1`
      icon.setAttribute("aria-hidden", "true")
      const split = ok && fromContactName && NAME_FIELDS.includes(target.value) && !stats.counts[target.value]
      const note = ok ? (split ? " (from Contact Name)" : "") : " (missing)"
      item.append(icon, document.createTextNode(`${this.requirementLabelFor(target, ok)}${note}`))
      return item
    })
    this.requiredListTarget.replaceChildren(intro, ...items)
  }

  requirementLabelFor (target, ok) {
    return ok ? target.label : this.requirementLabel(target)
  }

  // The blank-cell choice exists only in an update mode, and only for a column
  // that is actually mapped: an ignored or unmapped column writes nothing, so
  // its checkbox would be a promise about nothing. The cell stays (the table
  // keeps its columns); the check inside it is shown or hidden per row.
  toggleBlankColumn () {
    const applies = UPDATE_MODES.includes(this.currentMode())
    if (this.hasBlankHeaderTarget) this.blankHeaderTarget.hidden = !applies
    this.mappingItems().forEach((row) => {
      const cell = row.querySelector("[data-import-wizard-row='keepBlankCell']")
      if (!cell) return
      cell.hidden = !applies
      const value = this.draft.mapping?.[row.dataset.importWizardColumn] ?? ""
      const check = cell.querySelector(".gl-form-check")
      if (check) check.hidden = !value || value === IGNORE_VALUE
    })
    if (this.hasBlankHintTarget) this.blankHintTarget.hidden = applies
  }

  // ── Saved mappings (#19586) ───────────────────────────────────────────────
  // The draft carries `savedMapping` — the saved mapping in use for this file:
  // { id, name, how ("ghl" | "layout" | "picked"), newColumns, skipped, known,
  // total, ghlAccount, notice, dirty } — or null on automatic matching.
  // `notice` shows the green "Saved mapping applied" alert; `dirty` is any
  // edit since it was applied or saved (Continue then asks to save it).

  fileColumnKeys () {
    const headers = this.draft.file?.headers || []
    return headers.map((_header, index) => this.columnKey(headers, index))
  }

  fileColumnNames () {
    const headers = this.draft.file?.headers || []
    return headers.map((_header, index) => this.columnDisplayName(headers, index))
  }

  // Once per file: the server says which saved mapping, if any, fits it.
  // Failing quietly is right — automatic matching is already on screen.
  async matchSavedMapping () {
    this.draft.savedMappingChecked = true
    this.saveDraft()
    if (this.savedMappingsValue.length === 0 || !this.mappingsMatchUrlValue) return
    try {
      const response = await post(this.mappingsMatchUrlValue, {
        body: JSON.stringify({ columns: this.fileColumnKeys(), ghl_source_id: this.draft.file?.ghlSourceId || "" }),
        contentType: "application/json",
        responseKind: "json"
      })
      const payload = response.ok ? await this.responseJson(response) : null
      if (payload?.mapping) this.applySavedMapping(payload.mapping, payload.reason)
    } catch { /* stay on automatic matching */ }
  }

  applySavedMapping (mapping, how) {
    const applied = applySavedColumns(this.fileColumnKeys(), this.fileColumnNames(), mapping.columns || {})
    this.draft.mapping = applied.mapping
    this.draft.savedMapping = {
      id: mapping.id, name: mapping.name, how, ghlAccount: mapping.ghl_account || null,
      newColumns: applied.newColumns, skipped: applied.skipped, known: applied.known, total: applied.total,
      notice: true, dirty: false
    }
    this.draft.savedMappingUndone = false
    this.savedMappingFlash = ""
    this.reconcileMapping()
    this.saveDraft()
    this.refreshMapState()
  }

  // Undo: back to the wizard's own automatic matching.
  useAutomaticMatching () {
    if (!this.draft.file) return
    this.draft.mapping = this.autoMapping(this.draft.file.headers)
    this.draft.savedMapping = null
    this.draft.savedMappingUndone = true
    this.savedMappingFlash = ""
    this.saveDraft()
    this.refreshMapState()
  }

  savedMappingPicked (event) {
    const id = event.currentTarget.value
    if (!id) { this.useAutomaticMatching(); return }
    const mapping = this.savedMappingsValue.find((entry) => String(entry.id) === id)
    if (mapping) this.applySavedMapping(mapping, "picked")
  }

  markMappingEdited () {
    if (this.draft.savedMapping) this.draft.savedMapping.dirty = true
    this.savedMappingFlash = ""
  }

  renderSavedMapping () {
    if (!this.hasSavedRowTarget) return
    const saved = this.draft.savedMapping
    this.savedRowTarget.hidden = !this.draft.file
    this.renderSavedNotice(saved)
    if (this.hasSavedUndoneHintTarget) this.savedUndoneHintTarget.hidden = !(this.draft.savedMappingUndone && !saved)

    const hasSaved = this.savedMappingsValue.length > 0
    if (this.hasSavedPickerFieldTarget) this.savedPickerFieldTarget.hidden = !hasSaved
    if (this.hasManageMappingsButtonTarget) this.manageMappingsButtonTarget.hidden = !hasSaved
    if (this.hasSavedEmptyHintTarget) this.savedEmptyHintTarget.hidden = hasSaved
    if (this.hasSavedPickerTarget) this.renderSavedPicker(saved)
    if (this.hasSaveMappingButtonTarget) {
      if (saved && !saved.dirty) {
        this.disable(this.saveMappingButtonTarget, `No changes to save — “${saved.name}” is applied as saved`)
      } else {
        this.enable(this.saveMappingButtonTarget)
      }
    }
    if (this.hasSavedFlashTarget) {
      this.savedFlashTarget.textContent = this.savedMappingFlash || ""
      this.savedFlashTarget.hidden = !this.savedMappingFlash
    }
  }

  renderSavedPicker (saved) {
    const options = [["", "Automatic Matching"], ...this.savedMappingsValue.map((entry) => [String(entry.id), entry.name])]
    this.savedPickerTarget.replaceChildren(...options.map(([value, label]) => {
      const option = document.createElement("option")
      option.value = value
      option.textContent = label
      return option
    }))
    this.savedPickerTarget.value = saved?.id ? String(saved.id) : ""
  }

  renderSavedNotice (saved) {
    if (!this.hasSavedNoticeTarget) return
    this.savedNoticeTarget.hidden = !saved?.notice
    if (!saved?.notice) return
    this.setText("savedNoticeTitle", `Saved mapping applied: ${saved.name}`)
    this.setText("savedNoticeText", `${appliedSentence(saved)} Check any row before you continue.`)
    if (!this.hasSavedNoticeListTarget) return
    const bullets = appliedBullets(saved)
    this.savedNoticeListTarget.hidden = bullets.length === 0
    this.savedNoticeListTarget.replaceChildren(...bullets.map(([lead, names]) => {
      const item = document.createElement("li")
      item.append(document.createTextNode(lead))
      names.forEach((name, index) => {
        if (index > 0) item.append(document.createTextNode(", "))
        const strong = document.createElement("strong")
        strong.textContent = name
        item.append(strong)
      })
      if (names.length > 0) item.append(document.createTextNode("."))
      return item
    }))
  }

  // Keep the list in the server's order (by name, ignoring case).
  storeSavedMapping (mapping) {
    const rest = this.savedMappingsValue.filter((entry) => entry.id !== mapping.id)
    this.savedMappingsValue = [...rest, mapping]
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
  }

  // ── Save Mapping popup ──

  // `from` is SAVE_MAPPING_CONTINUE (the owner's question, Not Now, then on
  // to Review either way) or, from the Save Mapping button, an event.
  openSaveMapping (from) {
    if (!this.hasSaveMappingModalTarget) return
    this.saveMappingFrom = from === SAVE_MAPPING_CONTINUE ? SAVE_MAPPING_CONTINUE : SAVE_MAPPING_BUTTON
    const continuing = this.saveMappingFrom === SAVE_MAPPING_CONTINUE
    const saved = this.draft.savedMapping
    if (this.hasSaveMappingQuestionTarget) this.saveMappingQuestionTarget.hidden = !continuing
    if (this.hasSaveMappingSecondaryTarget) this.saveMappingSecondaryTarget.textContent = continuing ? "Not Now" : "Cancel"
    if (this.hasSaveMappingNameTarget) this.saveMappingNameTarget.value = saved?.name || ""
    this.setText("saveMappingHint", saved
      ? `Keep the name to update “${saved.name}”, or type a new name to save a copy.`
      : SAVE_MAPPING_NEW_HINT)
    this.hideSaveMappingError()
    this.gateSaveMapping()
    const modal = Modal.getOrCreateInstance(this.saveMappingModalTarget)
    this.saveMappingModalTarget.addEventListener("shown.bs.modal", () => this.optionalTarget("saveMappingName")?.focus(),
      { once: true })
    modal.show()
  }

  hideSaveMapping () {
    if (this.hasSaveMappingModalTarget) Modal.getOrCreateInstance(this.saveMappingModalTarget).hide()
  }

  // The name being kept means Update Mapping; a new name saves a copy.
  updatingSavedMapping (name) {
    const saved = this.draft.savedMapping
    return Boolean(saved?.id) && name.toLowerCase() === String(saved.name).toLowerCase()
  }

  saveMappingNameChanged () {
    this.hideSaveMappingError()
    this.gateSaveMapping()
  }

  // Held until a name is typed: the reason as the tooltip and as the footer
  // line the button is described by (INTERACTION-5).
  gateSaveMapping () {
    if (!this.hasSaveMappingSubmitTarget) return
    const name = this.hasSaveMappingNameTarget ? this.saveMappingNameTarget.value.trim() : ""
    this.setText("saveMappingSubmitLabel", this.updatingSavedMapping(name) ? "Update Mapping" : "Save Mapping")
    if (this.saveMappingBusy) return
    const button = this.saveMappingSubmitTarget
    button.disabled = !name
    if (name) button.removeAttribute("data-gl-tooltip")
    else button.setAttribute("data-gl-tooltip", MAPPING_NAME_REASON)
    if (this.hasSaveMappingReasonTarget) this.saveMappingReasonTarget.hidden = Boolean(name)
  }

  saveMappingDeclined () {
    this.hideSaveMapping()
    if (this.saveMappingFrom === SAVE_MAPPING_CONTINUE) this.goToReview()
  }

  goToReview () {
    this.saveDraft()
    this.visit(this.nextUrlValue)
  }

  async submitSaveMapping (event) {
    event?.preventDefault()
    if (this.saveMappingBusy || !this.hasSaveMappingSubmitTarget || this.saveMappingSubmitTarget.disabled) return
    const name = this.saveMappingNameTarget.value.trim()
    if (!name) return
    const updating = this.updatingSavedMapping(name)
    const body = JSON.stringify({ mapping: {
      name, columns: columnsForSave(this.fileColumnKeys(), this.draft.mapping),
      ghl_source_id: this.draft.file?.ghlSourceId || ""
    } })
    const options = { body, contentType: "application/json", responseKind: "json" }
    this.saveMappingBusy = true
    if (this.hasSaveMappingReasonTarget) this.saveMappingReasonTarget.hidden = true
    setButtonLoading(this.saveMappingSubmitTarget, true)
    let payload = null
    let ok = false
    try {
      const response = updating
        ? await patch(`${this.mappingsUrlValue}/${encodeURIComponent(this.draft.savedMapping.id)}`, options)
        : await post(this.mappingsUrlValue, options)
      payload = await this.responseJson(response)
      ok = response.ok && Boolean(payload?.mapping)
    } catch { ok = false }
    this.saveMappingBusy = false
    setButtonLoading(this.saveMappingSubmitTarget, false)
    if (!ok) {
      this.showSaveMappingError(payload?.errors?.[0] || SAVE_MAPPING_FAILED)
      this.gateSaveMapping()
      return
    }
    this.mappingSaved(payload.mapping, updating)
  }

  mappingSaved (mapping, updating) {
    this.storeSavedMapping(mapping)
    const previous = this.draft.savedMapping
    this.draft.savedMapping = {
      ...(previous || {}), id: mapping.id, name: mapping.name, ghlAccount: mapping.ghl_account || null,
      notice: false, dirty: false
    }
    this.draft.savedMappingUndone = false
    this.savedMappingFlash = updating ? `Updated “${mapping.name}”.` : `Saved as “${mapping.name}”.`
    this.saveDraft()
    this.hideSaveMapping()
    this.renderSavedMapping()
    if (this.saveMappingFrom === SAVE_MAPPING_CONTINUE) this.goToReview()
  }

  showSaveMappingError (message) {
    this.setText("saveMappingErrorText", message)
    if (this.hasSaveMappingErrorTarget) this.saveMappingErrorTarget.hidden = false
  }

  hideSaveMappingError () {
    if (this.hasSaveMappingErrorTarget) this.saveMappingErrorTarget.hidden = true
  }

  // ── Manage Mappings popup ──

  openManageMappings () {
    if (!this.hasManageMappingsModalTarget) return
    this.hideManageError()
    this.renderManageList()
    Modal.getOrCreateInstance(this.manageMappingsModalTarget).show()
  }

  renderManageList () {
    const build = (template) => this.savedMappingsValue.map((mapping) => this.buildManageItem(mapping, template))
    if (this.hasManageMappingsBodyTarget && this.hasManageRowTemplateTarget) {
      this.manageMappingsBodyTarget.replaceChildren(...build(this.manageRowTemplateTarget))
    }
    if (this.hasManageMappingsCardsTarget && this.hasManageCardTemplateTarget) {
      this.manageMappingsCardsTarget.replaceChildren(...build(this.manageCardTemplateTarget))
    }
  }

  buildManageItem (mapping, template) {
    const item = template.content.firstElementChild.cloneNode(true)
    item.dataset.mappingId = String(mapping.id)
    const part = (name) => item.querySelector(`[data-import-wizard-row='${name}']`)
    part("mappingName").textContent = mapping.name
    part("appliedWhen").textContent = appliedWhen(mapping)
    part("columnCount").textContent = String(mapping.column_count ?? Object.keys(mapping.columns || {}).length)
    const lastUsed = formatLastUsed(mapping.last_used_at, this.timeZoneValue)
    part("lastUsed").replaceChildren(lastUsed ? document.createTextNode(lastUsed) : emptyValueElement())
    const group = item.querySelector(".gl-row-actions")
    if (group) group.setAttribute("aria-label", `${mapping.name} actions`)
    part("renameButton")?.setAttribute("aria-label", `Rename ${mapping.name}`)
    part("deleteButton")?.setAttribute("aria-label", `Delete ${mapping.name}`)
    return item
  }

  manageItemFor (event) {
    const item = event.currentTarget.closest("[data-mapping-id]")
    const mapping = item && this.savedMappingsValue.find((entry) => String(entry.id) === item.dataset.mappingId)
    return mapping ? { item, mapping } : null
  }

  setRenaming (item, renaming) {
    const part = (name) => item.querySelector(`[data-import-wizard-row='${name}']`)
    part("mappingName").hidden = renaming
    part("renameInput").hidden = !renaming
    part("renameButton").hidden = renaming
    part("deleteButton").hidden = renaming
    part("saveRenameButton").hidden = !renaming
    part("cancelRenameButton").hidden = !renaming
  }

  startRename (event) {
    const found = this.manageItemFor(event)
    if (!found) return
    this.hideManageError()
    const input = found.item.querySelector("[data-import-wizard-row='renameInput']")
    input.value = found.mapping.name
    this.setRenaming(found.item, true)
    this.gateRename(found.item, found.mapping)
    input.focus()
    input.select?.()
  }

  // Save Name is held until the name differs from the saved one (BUTTON-9).
  gateRename (item, mapping) {
    const input = item.querySelector("[data-import-wizard-row='renameInput']")
    const name = input.value.trim()
    syncGateReason(item.querySelector("[data-import-wizard-row='saveRenameButton']"),
      { ready: Boolean(name) && name !== mapping.name, reason: RENAME_REASON })
  }

  renameChanged (event) {
    const found = this.manageItemFor(event)
    if (found) this.gateRename(found.item, found.mapping)
  }

  cancelRename (event) {
    event?.preventDefault()
    const found = this.manageItemFor(event)
    if (found) this.setRenaming(found.item, false)
  }

  async saveRename (event) {
    event?.preventDefault()
    const found = this.manageItemFor(event)
    if (!found) return
    const button = found.item.querySelector("[data-import-wizard-row='saveRenameButton']")
    if (button.disabled || button.getAttribute("aria-busy") === "true") return
    const name = found.item.querySelector("[data-import-wizard-row='renameInput']").value.trim()
    setButtonLoading(button, true)
    let payload = null
    let ok = false
    try {
      const response = await patch(`${this.mappingsUrlValue}/${encodeURIComponent(found.mapping.id)}`, {
        body: JSON.stringify({ mapping: { name } }), contentType: "application/json", responseKind: "json"
      })
      payload = await this.responseJson(response)
      ok = response.ok && Boolean(payload?.mapping)
    } catch { ok = false }
    setButtonLoading(button, false)
    if (!ok) {
      this.showManageError(payload?.errors?.[0] || SAVE_MAPPING_FAILED)
      this.gateRename(found.item, found.mapping)
      return
    }
    this.storeSavedMapping(payload.mapping)
    if (this.draft.savedMapping?.id === payload.mapping.id) {
      this.draft.savedMapping.name = payload.mapping.name
      this.saveDraft()
    }
    this.renderManageList()
    this.renderSavedMapping()
  }

  // Shared danger confirm (MODAL-6); leads already imported never change.
  async deleteMapping (event) {
    const found = this.manageItemFor(event)
    if (!found) return
    this.hideManageError()
    const confirmed = await showConfirmModal(
      `Delete “${found.mapping.name}”? Leads you already imported are not changed. This cannot be undone.`,
      { title: "Delete Saved Mapping?", confirmText: "Delete", variant: "danger" }
    )
    if (!confirmed) return
    let ok = false
    try {
      const response = await destroy(`${this.mappingsUrlValue}/${encodeURIComponent(found.mapping.id)}`,
        { responseKind: "json" })
      ok = response.ok
    } catch { ok = false }
    if (!ok) { this.showManageError(DELETE_MAPPING_FAILED); return }
    this.savedMappingsValue = this.savedMappingsValue.filter((entry) => entry.id !== found.mapping.id)
    if (this.draft.savedMapping?.id === found.mapping.id) {
      this.draft.savedMapping = null
      this.saveDraft()
    }
    this.renderManageList()
    this.renderSavedMapping()
    if (this.savedMappingsValue.length === 0 && this.hasManageMappingsModalTarget) {
      Modal.getOrCreateInstance(this.manageMappingsModalTarget).hide()
    }
  }

  showManageError (message) {
    this.setText("manageMappingsErrorText", message)
    if (this.hasManageMappingsErrorTarget) this.manageMappingsErrorTarget.hidden = false
  }

  hideManageError () {
    if (this.hasManageMappingsErrorTarget) this.manageMappingsErrorTarget.hidden = true
  }

  // ── Review ────────────────────────────────────────────────────────────────

  renderReview () {
    const hasFile = Boolean(this.draft.file)
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = hasFile
    if (this.hasMappingTableTarget) this.mappingTableTarget.hidden = !hasFile
    if (this.hasTagsCheckboxTarget && this.draft.addTags !== undefined) this.tagsCheckboxTarget.checked = this.draft.addTags
    if (this.hasWorkflowCheckboxTarget && this.draft.addToWorkflow !== undefined) {
      this.workflowCheckboxTarget.checked = this.draft.addToWorkflow
    }
    if (this.hasWorkflowSelectTarget && this.draft.workflowId) this.restoreWorkflowChoice()
    this.syncTags()
    this.syncWorkflow()
    this.renderReviewSummary()
    this.renderReviewMapping()
    this.syncSubmit()
    this.checkPhones()
    this.checkRequired()
  }

  renderReviewSummary () {
    if (!this.hasReviewSummaryTarget) return
    const file = this.draft.file
    const stats = this.mappingStats()
    const mode = this.modesValue.find((m) => m.value === this.currentMode())
    const keepCount = Object.entries(this.draft.keepBlank || {}).filter(([key, kept]) => {
      const value = this.draft.mapping?.[key] ?? ""
      return kept && value && value !== IGNORE_VALUE
    }).length
    const updating = UPDATE_MODES.includes(this.currentMode())
    const chosenTags = this.draft.addTags && Array.isArray(this.draft.tags) && this.draft.tags.length > 0
      ? this.draft.tags.join(", ")
      : ""
    // A column mapped to Tags (#18350) adds each row's own tags too.
    const tagsColumn = Object.values(this.draft.mapping || {}).includes(TAGS_COLUMN)
    const tags = [chosenTags, tagsColumn ? "each row's Tags column" : ""].filter(Boolean).join(" + ") || "None"
    const workflow = this.draft.addToWorkflow && this.draft.workflowName
      ? `${this.draft.workflowName} — enrollment runs after the import and can start its messages`
      : "None"
    const values = {
      file: file ? `${file.name} · ${file.rowCount} data ${file.rowCount === 1 ? "row" : "rows"}` : "No file chosen",
      order_name: this.draft.orderName?.trim() || "Not entered",
      lead_type: this.draft.leadTypeLabel || "General Life",
      cost: this.costEntered() ? `$${Number(this.draft.costPerLead).toFixed(2)} per lead (for your tracking; nothing is charged)` : "Not entered",
      vendor: this.draft.vendor || "Not chosen",
      mode: mode ? mode.label : "Create new leads only",
      match: this.currentMatchBy() === "email" ? "Email" : "Phone",
      blank: updating
        ? `Keep the existing value for ${keepCount} ${keepCount === 1 ? "column" : "columns"}; other blank cells overwrite`
        : "Not applicable — new leads only",
      columns: file
        ? `${stats.mapped} mapped · ${stats.ignored} ignored · ${stats.unmapped} not mapped (not imported)`
        : null,
      tags,
      workflow
    }
    Object.entries(values).forEach(([key, text]) => {
      const cell = this.reviewSummaryTarget.querySelector(`[data-import-wizard-summary='${key}']`)
      if (cell) setValueOrEmpty(cell, text)
    })
  }

  renderReviewMapping () {
    if (!this.hasReviewMappingBodyTarget || !this.draft.file) return
    const labels = Object.fromEntries(this.availableTargets().map((t) => [t.value, t.label]))
    const headers = this.draft.file.headers
    const rows = headers.map((_header, index) => {
      const key = this.columnKey(headers, index)
      const value = this.draft.mapping?.[key] ?? ""
      const row = document.createElement("tr")
      row.dataset.importWizardColumnIndex = String(index)
      const column = document.createElement("td")
      const strong = document.createElement("strong")
      strong.textContent = this.columnDisplayName(headers, index)
      column.append(strong)
      const samples = document.createElement("td")
      samples.className = "gl-wizard-map-samples"
      const sampleValues = this.draft.file.samples[index] || []
      if (sampleValues.length === 0) setValueOrEmpty(samples, null)
      else {
        samples.replaceChildren(...sampleValues.map((v) => {
          const d = document.createElement("div")
          d.dataset.importWizardSample = ""
          d.textContent = v
          return d
        }))
      }
      const field = document.createElement("td")
      if (value === IGNORE_VALUE) field.append(this.statusBadge({ label: "Ignored", tone: "secondary", icon: "ph-minus-circle" }))
      else if (!value) field.append(this.statusBadge({ label: "Not mapped — skipped", tone: "warning", icon: "ph-circle-dashed" }))
      else field.textContent = labels[value] || value
      row.append(column, samples, field)
      return row
    })
    this.reviewMappingBodyTarget.replaceChildren(...rows)
    this.renderReviewMappingCards(rows)
  }

  // The mobile-card twins of the review rows (#15175): the shared card
  // anatomy from reviewCardTemplate, filled with a copy of each row's cells so
  // the two views can never say different things.
  renderReviewMappingCards (rows) {
    if (!this.hasReviewMappingCardsTarget || !this.hasReviewCardTemplateTarget) return
    const cards = rows.map((row) => {
      const card = this.reviewCardTemplateTarget.content.firstElementChild.cloneNode(true)
      card.dataset.importWizardColumnIndex = row.dataset.importWizardColumnIndex
      const [column, samples, field] = row.children
      card.querySelector("[data-import-wizard-row='column']").textContent = column.textContent
      card.querySelector("[data-import-wizard-row='samples']").replaceChildren(...Array.from(samples.childNodes, (n) => n.cloneNode(true)))
      card.querySelector("[data-import-wizard-row='field']").replaceChildren(...Array.from(field.childNodes, (n) => n.cloneNode(true)))
      return card
    })
    this.reviewMappingCardsTarget.replaceChildren(...cards)
  }

  // ── Required-field row count (#17079) ─────────────────────────────────────

  requiredSummaryCell () {
    if (!this.hasReviewSummaryTarget) return null
    return this.reviewSummaryTarget.querySelector("[data-import-wizard-summary='required']")
  }

  // The targets the check reads: every required Lead column (the server's
  // `required` flag — RequiredFields), plus the Contact Name and Lead Type
  // columns that can stand in for them.
  requiredCheckTargets () {
    const required = this.targetsValue.filter((target) => target.required).map((target) => target.value)
    return [...required, CONTACT_NAME, LEAD_TYPE_COLUMN]
  }

  // The columns the check reads: { index: target } for each required target.
  requiredCheckMapping () {
    const headers = this.draft.file?.headers || []
    const targets = this.requiredCheckTargets()
    const mapping = {}
    headers.forEach((_header, index) => {
      const target = this.draft.mapping?.[this.columnKey(headers, index)] ?? ""
      if (targets.includes(target)) mapping[String(index)] = target
    })
    return mapping
  }

  // A CSV sends only the mapped required columns' cells, read from the text
  // this browser holds; a GHL file is counted where it is held.
  requiredCheckBody () {
    const mapping = this.requiredCheckMapping()
    const body = { mapping, lead_type: this.draft.leadType || "" }
    if (this.draft.file.ghlSourceId) return { ...body, ghl_source_id: this.draft.file.ghlSourceId }
    const text = this.loadCsvText()
    if (text === null) return null
    const { rows } = parseCsv(text)
    const columns = {}
    Object.keys(mapping).forEach((index) => {
      columns[index] = rows.slice(0, this.maxRowsValue || rows.length).map((row) => row[Number(index)] ?? "")
    })
    return { ...body, columns }
  }

  async checkRequired () {
    const cell = this.requiredSummaryCell()
    if (!cell || !this.draft.file || !this.requiredCheckUrlValue) return
    const body = this.requiredCheckBody()
    if (!body) { cell.textContent = REQUIRED_CHECK_STALE; return }

    cell.textContent = REQUIRED_CHECK_PENDING
    try {
      const response = await post(this.requiredCheckUrlValue, {
        body: JSON.stringify(body), contentType: "application/json", responseKind: "json"
      })
      if (!response.ok) throw new Error(`required check answered ${response.statusCode}`)
      const result = await response.json
      if (typeof result?.importable !== "number") throw new Error("required check answered without counts")
      this.renderRequiredCheck(result)
    } catch {
      cell.textContent = REQUIRED_CHECK_FAILED
    }
  }

  renderRequiredCheck ({ importable, failing, reasons }) {
    const cell = this.requiredSummaryCell()
    if (!cell) return
    const rows = (count) => `${count} ${count === 1 ? "row" : "rows"}`
    const parts = [
      this.statusBadge({ label: `${rows(importable)} will import`, tone: "success", icon: "ph-check-circle" })
    ]
    if (failing > 0) {
      parts.push(" ", this.statusBadge({ label: `${rows(failing)} will fail`, tone: "danger", icon: "ph-warning-circle" }))
      const list = document.createElement("ul")
      list.className = "gl-form-hint mb-0 mt-1"
      ;(reasons || []).forEach(({ reason, count }) => {
        const item = document.createElement("li")
        item.textContent = `${reason}: ${rows(count)}`
        list.append(item)
      })
      parts.push(list)
    }
    cell.replaceChildren(...parts)
  }

  // ── Undialable numbers (#14450) ───────────────────────────────────────────

  phoneColumnIndex () {
    const headers = this.draft.file?.headers || []
    return headers.findIndex((_header, index) => this.draft.mapping?.[this.columnKey(headers, index)] === "phone")
  }

  phoneSummaryCell () {
    if (!this.hasReviewSummaryTarget) return null
    return this.reviewSummaryTarget.querySelector("[data-import-wizard-summary='phones']")
  }

  async checkPhones () {
    const cell = this.phoneSummaryCell()
    if (!cell || !this.draft.file) return
    const index = this.phoneColumnIndex()
    if (index === -1) { cell.textContent = PHONE_CHECK_UNMAPPED; return }
    const stored = this.draft.file.phoneColumns
    if (!stored) { cell.textContent = PHONE_CHECK_STALE; return }
    const values = stored[String(index)]
    if (!values) { cell.textContent = PHONE_CHECK_NOT_PHONE_LIKE; return }

    cell.textContent = PHONE_CHECK_PENDING
    try {
      const response = await post(PHONE_CHECK_URL, {
        body: JSON.stringify({ phones: values }), contentType: "application/json", responseKind: "json"
      })
      if (!response.ok) throw new Error(`phone check answered ${response.statusCode}`)
      const { results } = await response.json
      if (!Array.isArray(results)) throw new Error("phone check answered without results")
      this.renderPhoneCheck(index, values, results)
    } catch {
      cell.textContent = PHONE_CHECK_FAILED
    }
  }

  renderPhoneCheck (index, values, results) {
    const cell = this.phoneSummaryCell()
    if (!cell) return
    const checked = results.length
    const flagged = results.filter((verdict) => verdict?.dialable === false).length
    const rowCount = this.draft.file.rowCount
    const scope = checked < rowCount ? ` (first ${checked} of ${rowCount} rows checked)` : ""
    if (flagged === 0) {
      cell.replaceChildren(
        this.statusBadge({ label: "All can be dialed", tone: "success", icon: "ph-check-circle" }),
        document.createTextNode(` ${checked} ${checked === 1 ? "number" : "numbers"} checked${scope}.`)
      )
    } else {
      cell.replaceChildren(
        this.statusBadge({ label: `${flagged} can't be dialed`, tone: "warning", icon: "ph-phone-x" }),
        document.createTextNode(
          ` ${flagged} of ${checked} ${checked === 1 ? "number" : "numbers"} can't be dialed${scope}. You can still import them.`
        )
      )
    }
    this.badgePreviewedPhones(index, values, results)
  }

  // The preview shows the first SAMPLE_ROWS non-empty values of each column,
  // in file order; walk the same rows so each badge lands on its own sample.
  // Both the review row and its mobile card (#15175) carry the badges.
  badgePreviewedPhones (index, values, results) {
    const containers = [
      this.hasReviewMappingBodyTarget && this.reviewMappingBodyTarget,
      this.hasReviewMappingCardsTarget && this.reviewMappingCardsTarget
    ].filter(Boolean)
    containers.forEach((container) => {
      const item = container.querySelector(`[data-import-wizard-column-index='${index}']`)
      if (item) this.badgePreviewedPhoneSamples(item, values, results)
    })
  }

  badgePreviewedPhoneSamples (row, values, results) {
    const samples = Array.from(row.querySelectorAll("[data-import-wizard-sample]"))
    let sample = 0
    for (let r = 0; r < Math.min(SAMPLE_ROWS, values.length) && sample < samples.length; r++) {
      if (values[r] === "") continue
      const verdict = results[r]
      if (verdict?.dialable === false) {
        const hint = document.createElement("div")
        hint.className = "gl-form-hint"
        hint.textContent = `This number can't be dialed (${verdict.reason || "it looks like a placeholder number"}). You can still import it.`
        samples[sample].append(" ", this.statusBadge({ label: "Can't be dialed", tone: "warning", icon: "ph-phone-x" }), hint)
      }
      sample++
    }
  }

  syncTags () {
    if (!this.hasTagsCheckboxTarget) return
    this.draft.addTags = this.tagsCheckboxTarget.checked
    this.saveDraft()
    this.pushTagPickerState()
    this.renderReviewSummary()
  }

  // tag-list-picker:change — the names chosen (or just created) in the picker.
  tagsPicked (event) {
    const tags = event.detail?.tags
    this.draft.tags = Array.isArray(tags) ? tags.slice() : []
    this.saveDraft()
    if (this.stepValue === "verify") this.renderReviewSummary()
  }

  // tag-list-picker:ready (the island mounted) and every checkbox change:
  // hand the picker the saved names and whether it is live. An unticked box
  // greys it out and keeps the names.
  pushTagPickerState () {
    if (!this.hasTagsPickerTarget) return
    this.tagsPickerTarget.dispatchEvent(new CustomEvent(TAG_PICKER_SET_EVENT, {
      detail: {
        tags: Array.isArray(this.draft.tags) ? this.draft.tags.slice() : [],
        disabled: !this.draft.addTags
      }
    }))
  }

  // A saved choice is restored only while it is still offered: a Workflow that
  // started finishing since (#16609) is listed disabled and takes no new leads.
  restoreWorkflowChoice () {
    const option = Array.from(this.workflowSelectTarget.options).find((candidate) => candidate.value === String(this.draft.workflowId))
    if (option && !option.disabled) {
      this.workflowSelectTarget.value = this.draft.workflowId
      return
    }
    this.workflowSelectTarget.value = ""
    this.draft.workflowId = ""
    this.draft.workflowName = ""
  }

  syncWorkflow () {
    if (!this.hasWorkflowCheckboxTarget || !this.hasWorkflowSelectTarget) return
    const enabled = this.workflowCheckboxTarget.checked
    this.workflowSelectTarget.disabled = !enabled
    this.draft.addToWorkflow = enabled
    this.saveDraft()
    this.renderReviewSummary()
  }

  // A11Y-1 / BUTTON-4: Start Import always explains why it is disabled: a
  // missing file, an unfinished Upload step, a file whose text this browser no
  // longer holds, an incomplete mapping, then consent.
  syncSubmit () {
    if (!this.hasSubmitButtonTarget || this.submitting) return
    let reason = null
    if (!this.draft.file) reason = NO_FILE_REASON
    else if (!this.uploadComplete()) reason = SETTINGS_REASON
    else if (!this.draft.file.ghlSourceId && this.loadCsvText() === null) reason = STALE_FILE_REASON
    else if (this.mappingBlocker(this.mappingStats())) reason = this.mappingBlocker(this.mappingStats())
    else if (!this.hasTcpaCheckboxTarget || !this.tcpaCheckboxTarget.checked) reason = CONSENT_REASON

    if (reason) this.disable(this.submitButtonTarget, reason)
    else this.enable(this.submitButtonTarget)
    if (this.hasSubmitReasonTarget) {
      this.submitReasonTarget.textContent = reason ? `Start Import is unavailable: ${reason}.` : "Ready to import."
    }
  }

  // ── Start Import ──────────────────────────────────────────────────────────

  // One POST carries the file and every choice (#15364). The server decides:
  // a 422 lists every reason in the one error alert (FORM-5), success clears
  // the draft and opens the Results step for the new import. The busy state
  // (BUTTON-8) plus `submitting` make a double click harmless; the server also
  // refuses a second import while one is running.
  async startImport (event) {
    event?.preventDefault()
    if (this.submitting || !this.hasSubmitButtonTarget || this.submitButtonTarget.disabled) return
    const text = this.draft.file?.ghlSourceId ? null : this.loadCsvText()
    if (text === null && !this.draft.file?.ghlSourceId) { this.syncSubmit(); return }

    this.submitting = true
    this.hideSubmitErrors()
    setButtonLoading(this.submitButtonTarget, true)
    try {
      const response = await post(this.createUrlValue, { body: this.buildFormData(text), responseKind: "json" })
      const payload = await this.responseJson(response)
      if (response.ok && payload?.redirect_url) {
        this.resetDraft()
        this.visit(payload.redirect_url)
        return
      }
      this.showSubmitErrors(Array.isArray(payload?.errors) && payload.errors.length > 0 ? payload.errors : [SUBMIT_FAILED])
    } catch {
      this.showSubmitErrors([SUBMIT_FAILED])
    }
    this.submitting = false
    setButtonLoading(this.submitButtonTarget, false)
    this.syncSubmit()
  }

  async responseJson (response) {
    try { return await response.json } catch { return null }
  }

  buildFormData (text) {
    const form = new FormData()
    const file = this.draft.file
    if (file.ghlSourceId) form.append("ghl_import_source_id", file.ghlSourceId)
    else form.append("csv_file", new Blob([text], { type: "text/csv" }), file.name)
    form.append("order_name", (this.draft.orderName || "").trim())
    form.append("lead_type", this.draft.leadType || "")
    form.append("cost_per_lead", this.draft.costPerLead || "")
    form.append("lead_vendor_id", this.draft.vendorId || "")
    form.append("import_mode", this.currentMode())
    form.append("match_by", this.currentMatchBy())
    form.append("tcpa_ack", this.hasTcpaCheckboxTarget && this.tcpaCheckboxTarget.checked ? "1" : "0")
    const updating = UPDATE_MODES.includes(this.currentMode())
    file.headers.forEach((_header, index) => {
      const key = this.columnKey(file.headers, index)
      const target = this.draft.mapping?.[key] ?? ""
      if (!target || target === IGNORE_VALUE) return
      form.append(`column_mapping[${index}]`, target)
      if (updating && this.draft.keepBlank?.[key]) form.append("keep_blank_columns[]", String(index))
    })
    if (this.draft.addTags) (this.draft.tags || []).forEach((tag) => form.append("tag_names[]", tag))
    if (this.draft.addToWorkflow && this.draft.workflowId) form.append("crm_workflow_id", this.draft.workflowId)
    if (this.draft.savedMapping?.id) form.append("saved_mapping_id", String(this.draft.savedMapping.id))
    return form
  }

  showSubmitErrors (messages) {
    if (!this.hasSubmitErrorsTarget) return
    if (this.hasSubmitErrorListTarget) {
      this.submitErrorListTarget.replaceChildren(...messages.map((message) => {
        const item = document.createElement("li")
        item.textContent = message
        return item
      }))
    }
    this.submitErrorsTarget.hidden = false
  }

  hideSubmitErrors () {
    if (this.hasSubmitErrorsTarget) this.submitErrorsTarget.hidden = true
  }

  // ── Results / shared ──────────────────────────────────────────────────────

  resetDraft () {
    this.draft = this.emptyDraft()
    try {
      window.sessionStorage.removeItem(STORAGE_KEY)
      window.sessionStorage.removeItem(CSV_STORAGE_KEY)
    } catch { /* storage unavailable */ }
  }

  // How a row finds an existing lead (#16803): phone unless the agent chose email.
  currentMatchBy () {
    return this.draft.matchBy === "email" ? "email" : "phone"
  }

  // Every Upload-step choice the server requires: file, order name, lead
  // type and one of the agent's vendors. The cost is optional, but when
  // entered it must be $0 or more.
  uploadComplete () {
    return Boolean(this.draft.file) && Boolean(this.draft.orderName?.trim()) &&
      Boolean(this.draft.leadType) && this.validCost() && Boolean(this.draft.vendorId)
  }

  costEntered () {
    return String(this.draft.costPerLead ?? "").trim() !== "" && this.validCost()
  }

  validCost () {
    const raw = String(this.draft.costPerLead ?? "").trim()
    if (raw === "") return true
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) return false
    return this.maxCostValue <= 0 || Number(raw) <= this.maxCostValue
  }

  visit (url) {
    if (!url) return
    if (window.Turbo && typeof window.Turbo.visit === "function") window.Turbo.visit(url)
    else window.location.assign(url)
  }

  // Disabled controls carry the reason as data-gl-tooltip plus an accessible
  // name of "<label> — <reason>" (the card_form_actions / card-dirty shape),
  // and lose both the moment they enable — the server paints the same pair for
  // the first frame.
  disable (button, reason) {
    const label = this.authoredLabel(button)
    button.disabled = true
    button.setAttribute("data-gl-tooltip", reason)
    button.setAttribute("aria-label", `${label}${NAME_SEPARATOR}${reason}`)
  }

  enable (button) {
    this.authoredLabel(button)
    button.disabled = false
    button.removeAttribute("data-gl-tooltip")
    button.removeAttribute("aria-label")
  }

  authoredLabel (button) {
    if (!button.dataset.importWizardLabel) button.dataset.importWizardLabel = button.textContent.trim()
    return button.dataset.importWizardLabel
  }

  statusBadge ({ label, tone, icon }) {
    const badge = document.createElement("span")
    badge.className = statusBadgeClass(tone)
    if (icon) {
      const glyph = document.createElement("i")
      glyph.className = `ph ${icon} me-1`
      glyph.setAttribute("aria-hidden", "true")
      badge.append(glyph)
    }
    badge.append(document.createTextNode(label))
    return badge
  }

// The draft mapping / keepBlank key for one CSV column. The header text
// stays the key when it is unique — that is the readable payload shape the
// backend handoff runbook documents — but a blank header and a header the
// file repeats (two "Phone" columns are common in real exports) are keyed
// by position, so two rows never read and write the same draft entry.
columnKey (headers, index) {
  const header = headers[index]
  if (header === "") return `__column_${index}`
  return headers.indexOf(header) === index ? header : `${header}__column_${index}`
}

columnDisplayName (headers, index) {
  const header = headers[index]
  if (header === "") return `Column ${index + 1} (no header)`
  return headers.indexOf(header) === index ? header : `${header} (column ${index + 1})`
}

  currentMode () {
    return (this.hasModeTarget && this.modeTarget.value) || this.draft.mode || "create_only"
  }

  // The targets offered for the chosen lead type: every one without a
  // lead_class, plus that type's own fields.
  availableTargets () {
    return this.targetsValue.filter((t) => !t.lead_class || t.lead_class === this.draft.leadType)
  }

  targetValues () {
    return this.availableTargets().map((t) => t.value)
  }

  // ── Draft storage (sessionStorage; there is no server draft) ──────────────

  emptyDraft () {
    return {
      source: CSV_SOURCE, file: null, mapping: {}, keepBlank: {}, orderName: "", leadType: "", leadTypeLabel: "",
      costPerLead: "", vendorId: "", vendor: "", mode: "create_only", tags: []
    }
  }

  loadDraft () {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY)
      const parsed = raw ? JSON.parse(raw) : null
      return parsed && typeof parsed === "object" ? { ...this.emptyDraft(), ...parsed } : this.emptyDraft()
    } catch {
      return this.emptyDraft()
    }
  }

  saveDraft () {
    try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.draft)) } catch { /* storage unavailable */ }
  }

  // The file's text, kept apart from the draft so the draft stays small.
  // Returns false when the browser cannot store it (quota, private mode).
  saveCsvText (text) {
    try { window.sessionStorage.setItem(CSV_STORAGE_KEY, text); return true } catch { return false }
  }

  loadCsvText () {
    try { return window.sessionStorage.getItem(CSV_STORAGE_KEY) } catch { return null }
  }

  clearCsvText () {
    try { window.sessionStorage.removeItem(CSV_STORAGE_KEY) } catch { /* storage unavailable */ }
  }
}
